import { paginationMetaSchema, type PaginationMeta } from '@steado/admin-contract/pagination';
import {
  adminUserCoachSchema,
  adminUserDetailSchema,
  adminUserHealthSchema,
  adminUserLogsSchema,
  adminUserPlanSchema,
  adminUserTranscriptSchema,
  foodScanSchema,
  planSafetyHoldSchema,
  returnDecisionSchema,
  userEntitlementSchema,
  type GrantEntitlement,
  type PlanSafetyHoldView,
  type UserEntitlementView,
  type AdminUserCoach,
  type AdminUserDetail,
  type AdminUserHealth,
  type AdminUserLogs,
  type AdminUserPlan,
  type AdminUserTranscript,
  type FoodScanView,
  type RecordReturnDecision,
  type ReturnDecisionView,
} from '@steado/admin-contract/user-detail';
import { adminUserAiSpendSchema } from '@steado/admin-contract/ai-ops';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient } from '@/lib/api/client';

/**
 * One query per tab, not one query for the screen.
 *
 * Each tab is a separate round trip so opening a user does not pay for a
 * transcript index and thirty days of meal logs nobody asked to see. TanStack
 * Query caches per key, so switching back to a tab is free.
 */

export const SCANS_PAGE_SIZE = 20;

export function userDetailQueryOptions(api: ApiClient, userId: string) {
  return queryOptions({
    queryKey: ['admin', 'user', userId, 'detail'],
    queryFn: (): Promise<AdminUserDetail> =>
      api.request({ path: `/admin/users/${userId}`, schema: adminUserDetailSchema }),
  });
}

export function userPlanQueryOptions(api: ApiClient, userId: string, enabled: boolean) {
  return queryOptions({
    queryKey: ['admin', 'user', userId, 'plan'],
    queryFn: (): Promise<AdminUserPlan> =>
      api.request({ path: `/admin/users/${userId}/plan`, schema: adminUserPlanSchema }),
    enabled,
  });
}

export function userLogsQueryOptions(api: ApiClient, userId: string, enabled: boolean) {
  return queryOptions({
    queryKey: ['admin', 'user', userId, 'logs'],
    queryFn: (): Promise<AdminUserLogs> =>
      api.request({ path: `/admin/users/${userId}/logs`, schema: adminUserLogsSchema }),
    enabled,
  });
}

export function userCoachQueryOptions(api: ApiClient, userId: string, enabled: boolean) {
  return queryOptions({
    queryKey: ['admin', 'user', userId, 'coach'],
    queryFn: (): Promise<AdminUserCoach> =>
      api.request({ path: `/admin/users/${userId}/coach`, schema: adminUserCoachSchema }),
    enabled,
  });
}

export function userTranscriptQueryOptions(api: ApiClient, userId: string, sessionId: string | null) {
  return queryOptions({
    queryKey: ['admin', 'user', userId, 'coach', sessionId],
    queryFn: (): Promise<AdminUserTranscript> =>
      api.request({
        path: `/admin/users/${userId}/coach/${sessionId ?? ''}`,
        schema: adminUserTranscriptSchema,
      }),
    enabled: sessionId !== null,
  });
}

export interface ScansPage {
  readonly items: FoodScanView[];
  readonly pagination: PaginationMeta;
}

const scansPageSchema = z.object({
  items: z.array(foodScanSchema),
  pagination: paginationMetaSchema,
});

export function userScansQueryOptions(
  api: ApiClient,
  userId: string,
  cursor: string | undefined,
  enabled: boolean,
) {
  return queryOptions({
    queryKey: ['admin', 'user', userId, 'scans', cursor ?? null],
    queryFn: async (): Promise<ScansPage> => {
      const response = await api.requestWithMeta({
        path: `/admin/users/${userId}/scans`,
        query: { cursor, limit: SCANS_PAGE_SIZE },
        schema: z.array(foodScanSchema),
      });

      return scansPageSchema.parse({ items: response.data, pagination: response.meta.pagination });
    },
    enabled,
    placeholderData: (previous) => previous,
  });
}

/**
 * Not a query.
 *
 * Revealing health data writes an audit row on the server, so it must happen
 * exactly when an operator asks and never as a cache refill, a retry or a
 * background refetch. Modelled as a mutation for that reason, even though the
 * verb is GET.
 */
export function revealHealth(api: ApiClient, userId: string): Promise<AdminUserHealth> {
  return api.request({
    path: `/admin/users/${userId}/health`,
    query: { reveal: 'true' },
    schema: adminUserHealthSchema,
  });
}

/**
 * Records what a user may do with an injured joint again.
 *
 * Append-only on the server, so this is never an idempotent write and must not
 * be retried automatically: a second attempt writes a second decision, and a
 * history is exactly what this table exists to keep.
 */
/**
 * Ends a plan safety hold.
 *
 * Idempotent on the server, unlike the append-only return decision beside it:
 * a second call finds the work already done and answers the current state, so
 * this one is safe to retry.
 */
export function liftPlanSafetyHold(api: ApiClient, userId: string): Promise<PlanSafetyHoldView> {
  return api.request({
    path: `/admin/users/${userId}/plan-safety-hold/lift`,
    method: 'POST',
    schema: planSafetyHoldSchema,
  });
}

/**
 * Grants Premium without a store purchase — a `promotional` subscription row.
 *
 * Idempotent in effect (granting twice is still one grant), so safe to retry.
 * `expiresAt` optional: absent grants until revoked; the API's expiry sweep
 * demotes within minutes of a set expiry passing.
 */
export function grantEntitlement(
  api: ApiClient,
  userId: string,
  body: GrantEntitlement,
): Promise<UserEntitlementView> {
  return api.request({
    path: `/admin/users/${userId}/entitlement/grant`,
    method: 'POST',
    body,
    schema: userEntitlementSchema,
  });
}

/**
 * What one person cost in model time.
 *
 * Its own request rather than part of the detail payload: the aggregates it
 * runs are windowed GROUP BYs over `llm_calls`, and making every visit to the
 * Profile tab pay for them would be a slow screen for a number most visits
 * never look at.
 */
export function userAiSpendQueryOptions(api: ApiClient, userId: string, days: number) {
  return queryOptions({
    queryKey: ['admin', 'user-ai-spend', userId, days],
    queryFn: () =>
      api.request({
        path: `/admin/users/${userId}/ai-spend?days=${String(days)}`,
        schema: adminUserAiSpendSchema,
      }),
  });
}

/** Ends it now. Idempotent server-side: a second call finds the work done. */
export function revokeEntitlement(api: ApiClient, userId: string): Promise<UserEntitlementView> {
  return api.request({
    path: `/admin/users/${userId}/entitlement/revoke`,
    method: 'POST',
    schema: userEntitlementSchema,
  });
}

export function recordReturnDecision(
  api: ApiClient,
  userId: string,
  body: RecordReturnDecision,
): Promise<ReturnDecisionView> {
  return api.request({
    path: `/admin/users/${userId}/return-decisions`,
    method: 'POST',
    body,
    schema: returnDecisionSchema,
  });
}
